import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../utils/theme';
import { API_BASE_URL } from '../utils/ApiManager';

const VideoViewer = ({ video, onBack, bucketId }) => {
  // Change from 'videoViewer' to 'videoviewer' to match the actual namespace in i18n.js
  const { t } = useTranslation('videoviewer');
  const [isDownloading, setIsDownloading] = useState(false);

  // Helper function to get secure video URL
  const getSecureVideoUrl = (videoUrl) => {
    if (!videoUrl) return '';
    
    // Handle secure URLs (from the new system)
    if (videoUrl.startsWith('/api/files/access')) {
      return `${API_BASE_URL}${videoUrl}`;
    }
    
    // Handle absolute paths (starting with /downloads/) - legacy support during transition
    if (videoUrl.startsWith('/downloads/')) {
      return `${API_BASE_URL}${videoUrl}`;
    }
    
    // Handle relative paths (just filename, without /downloads/) - legacy support
    if (videoUrl && !videoUrl.includes('/')) {
      return `${API_BASE_URL}/downloads/${videoUrl}`;
    }

    // Default case: return the URL as is
    return videoUrl;
  };

  // Format metadata if present
  const metadata = video.metadata ? 
    (typeof video.metadata === 'string' ? JSON.parse(video.metadata) : video.metadata) 
    : null;
  
  const videoSrc = video.url || (video.filePath ? video.filePath.replace(/^.*[\\\/]/, '') : '');
  const secureVideoUrl = getSecureVideoUrl(videoSrc);
  
  // Extract filename from video source for download attribute
  const getFileName = () => {
    if (!videoSrc) return 'video.mp4';
    const parts = videoSrc.split('/');
    return parts[parts.length - 1] || 'video.mp4';
  };
  
  // Handle direct download with fetch + blob approach
  const handleDownload = async (e) => {
    e.preventDefault();
    
    if (!secureVideoUrl) return;
    
    try {
      setIsDownloading(true);
      
      // Fetch the video file
      const response = await fetch(secureVideoUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status}`);
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to force the download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFileName();
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert(t('downloadError', 'There was a problem downloading the video. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation bar with back and download button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onBack(bucketId)}
          className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} flex items-center`}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('back')}
        </button>
        
        {/* Small download button in header */}
        {secureVideoUrl && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.sm} flex items-center ${isDownloading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isDownloading ? (
              <>
                <svg className="animate-spin w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('downloading')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('download')}
              </>
            )}
          </button>
        )}
      </div>
      
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="p-6">
          <div className="w-full max-w-3xl mx-auto aspect-w-16 aspect-h-9 mb-4 relative">
            <video
              src={secureVideoUrl}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              className="rounded-lg w-full h-full object-contain bg-black"
              controlsList="nodownload"
              onError={(e) => {
                console.error('Video playback error:', e);
                alert(t('playbackError', 'There was a problem playing the video. Please try again.'));
              }}
            >
              <source src={secureVideoUrl} type="video/mp4" />
              {t('videoNotSupported')}
            </video>
          </div>
          
          {/* Prominent download button BELOW video - with orange accent color */}
          {secureVideoUrl && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className={`${theme.button.base} py-3 px-6 bg-[#FFBA00] hover:bg-[#E6A800] text-white rounded-lg shadow-md transition-all duration-200 flex items-center justify-center ${isDownloading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isDownloading ? (
                  <>
                    <svg className="animate-spin w-5 h-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('downloadingVideo')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('downloadVideo')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoViewer;